import * as React from "react";
import { Button, Dropdown, Tooltip } from "antd";
import { ChevronDown, RefreshCw } from "react-feather";
import { linksOf } from "../../services/cpln";
import { useNavigate, useLocation } from "react-router-dom";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { formatStrings } from "../../utils/time";
import { itemHelpers as itemHelpersBase } from "../../mst/kinds/item.helpers";
import { Org } from "../../schema/types/org/entity";
import { useGetOrgQuery } from "../../store/api/orgApi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { dateString } from "../../components/dateStringFn";
import { DateString } from "../../components/dateString";
import { LastUpdated } from "../../components/LastUpdated";
import { ViewModal } from "../../components/modals/viewModal";
import { TerraformImportModal } from "../../components/modals/tfImportModal";
import { K8sCrdExportModal } from "../../components/modals/k8sCrdExportModal";
import { selectContext } from "../../store/contextSlice";
import { selectLabel } from "../../store/timezoneSlice";
import { Kind } from "../../schema/types/base";
import { applyActions } from "../../store/applySlice";

interface Props {
  org: Org;
}
export const OrgDetailHeader: React.FC<Props> = ({ org }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { refetch, fulfilledTimeStamp, isFetching } = useGetOrgQuery(org.name, { skip: !org.name });

  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);
  const context = useAppSelector(selectContext);
  const timezoneLabel = useAppSelector(selectLabel);
  const terraformSupportedKinds = useAppSelector((state) => state.rest.terraformSupportedKinds);
  const k8sCrdSupportedKinds = useAppSelector((state) => state.rest.k8sCrdSupportedKinds);

  const [timeKey, setTimeKey] = React.useState(Date.now());
  const [terraformImportCommand, setTerraformImportCommand] = React.useState("");
  const [action, setAction] = React.useState<"edit" | "view" | "tfImportCommand" | "k8sCrdExport" | "">("");

  const kind: any = org.kind;
  const itemHelpers = itemHelpersBase({ org: context.org, gvc: context.gvc, kind: kind, name: org.name });
  const selfLink = linksOf(org).self!;

  const canExportToTerraform = terraformSupportedKinds.includes(kind as Kind);
  const canExportToK8sCrd = k8sCrdSupportedKinds.includes(kind as Kind);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeKey(Date.now());
    }, 1 * 60 * 1_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    if (terraformImportCommand) {
      setAction("tfImportCommand");
    }
  }, [terraformImportCommand]);

  async function handleActionsClick({ key }: any) {
    switch (key) {
      case "edit":
        editApply();
        break;
      case "view":
        setAction("view");
        break;
      case "export-json":
        await itemHelpers.handleExportJSON([selfLink], false);
        break;
      case "export-yaml":
        await itemHelpers.handleExportYaml([selfLink], false);
        break;
      case "export-json-slim":
        await itemHelpers.handleExportJSON([selfLink], true);
        break;
      case "export-yaml-slim":
        await itemHelpers.handleExportYaml([selfLink], true);
        break;
      case "export-terraform":
        setTerraformImportCommand(await itemHelpers.handleExportTerraform([selfLink]));
        break;
      case "export-k8s-crd":
        setAction("k8sCrdExport");
        break;
      default:
        break;
    }
  }

  async function editApply() {
    dispatch(applyActions.initialize({ itemLink: selfLink, previousPath: pathname }));
    navigate(`/console/apply`);
  }

  const items: ItemType<MenuItemType>[] = [];
  items.push({ key: "edit", label: "Edit & Apply" });
  items.push({ key: "view", label: "View" });
  const exportItem = {
    key: "ignore2",
    label: "Export",
    children: [
      { key: "export-json", label: "JSON" },
      { key: "export-yaml", label: "YAML" },
      { key: "export-json-slim", label: "JSON Slim" },
      { key: "export-yaml-slim", label: "YAML Slim" },
    ],
  };
  if (canExportToTerraform) {
    exportItem.children.push({ key: "export-terraform", label: "Terraform" });
  }
  if (canExportToK8sCrd) {
    exportItem.children.push({ key: "export-k8s-crd", label: "K8s CRD" });
  }
  items.push(exportItem);

  function viewModalOnClose() {
    setAction("");
  }

  return (
    <>
      <div className="n-detail-header flex items-center justify-between pb-4 h-20 z-10">
        <div>
          <div data-testid="detail-kind" className="text-dark text-xs">
            {kind.toUpperCase()}
          </div>
          <div className="text-2xl font-semibold leading-none">{org.name}</div>
          <LastUpdated prefix="Fetched" timestamp={fulfilledTimeStamp} />
        </div>
        <div className="flex-grow" />
        <div className="flex items-center gap-2">
          <div>{`Version ${org.version}`}</div>
          <Button
            disabled={isFetching}
            loading={isFetching}
            onClick={() => refetch()}
            icon={<RefreshCw size={17} />}
            size="small"
            type="default"
          />
          {items.length > 0 ? (
            <Dropdown trigger={["click"]} menu={{ items: items, className: "menu", onClick: handleActionsClick }}>
              <Button type={"primary"} size="small" icon={<ChevronDown />} iconPosition="end">
                Actions
              </Button>
            </Dropdown>
          ) : null}
          <>
            <Tooltip
              title={[
                `Created: ${dateString(org.created, { format: formatStrings.humanizeWAgo })}`,
                `Created (${timezoneLabel}): ${dateString(org.created, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Created (UTC): ${dateString(org.created, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
                `Last Modified: ${dateString(org.lastModified, { format: formatStrings.humanizeWAgo })}`,
                `Last Modified (${timezoneLabel}): ${dateString(org.lastModified, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Last Modified (UTC): ${dateString(org.lastModified, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
              ]}
            >
              <div key={timeKey} className="ml-2">
                <DateString prefix={"Updated"} iso={org.lastModified} format={formatStrings.humanizeWAgo} />
              </div>
            </Tooltip>
          </>
        </div>
      </div>
      {action === "view" ? (
        <ViewModal
          kind={kind}
          link={selfLink}
          visible={true}
          onClose={viewModalOnClose}
          title={org.name}
          filename={itemHelpers.getFileName()}
        />
      ) : action === "tfImportCommand" ? (
        <TerraformImportModal command={terraformImportCommand} onClose={() => setAction("")} />
      ) : action === "k8sCrdExport" ? (
        <K8sCrdExportModal
          kind={kind}
          links={[selfLink]}
          onClose={() => {
            setAction("");
          }}
        />
      ) : null}
    </>
  );
};
